import { useEffect, useState } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Briefcase, Users, Wallet, CalendarCheck, Coins, Plus, Trash2, Pencil, Check, X, TrendingDown, TrendingUp, FileText, History } from "lucide-react";
import { toast } from "sonner";

const monthNow = () => new Date().toISOString().slice(0, 7);
const dayNow = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

const Stat = ({ label, value, accent, icon: Icon }) => (
  <div className="border border-border p-3 bg-background">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">{Icon && <Icon className={`h-3 w-3 ${accent || ""}`} />}{label}</div>
    <div className={`font-mono-num text-lg font-bold mt-1 ${accent || ""}`}>{typeof value === "number" ? money(value) : value}</div>
  </div>
);

export default function OfficeManagementPage() {
  const [month, setMonth] = useState(monthNow());
  const [overhead, setOverhead] = useState(null);

  const loadOverhead = () => api.get(`/office/overhead?month=${month}`).then((r) => setOverhead(r.data)).catch(() => {});
  useEffect(() => { loadOverhead(); /* eslint-disable-next-line */ }, [month]);

  return (
    <div className="p-6 space-y-6" data-testid="office-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-2"><Briefcase className="h-7 w-7 text-primary" /> Office Management</h1>
          <p className="text-muted-foreground text-sm">Staff, monthly fixed costs, attendance &amp; cash ledger — summarised into your monthly overhead.</p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Overhead Month</Label>
          <Input type="month" className="rounded-none bg-card h-10 w-[170px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="office-month" />
        </div>
      </div>

      {overhead && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="office-overhead">
          <Stat label="Fixed Costs" value={overhead.fixed_total} icon={Wallet} />
          <Stat label="Cash Paid" value={overhead.cash_paid} accent="text-primary" icon={TrendingDown} />
          <Stat label="Total Overhead" value={overhead.total_overhead} accent="text-red-500" icon={Coins} />
          <Stat label="Booking Profit" value={overhead.booking_profit} accent="text-emerald-500" icon={TrendingUp} />
          <Stat label="Net Margin" value={overhead.net_margin} accent={(overhead.net_margin || 0) >= 0 ? "text-emerald-500" : "text-red-500"} icon={(overhead.net_margin || 0) >= 0 ? TrendingUp : TrendingDown} />
          <Stat label="Attendance" value={`${overhead.present}P / ${overhead.absent}A`} accent="text-blue-500" icon={CalendarCheck} />
        </div>
      )}

      <Tabs defaultValue="staff" className="w-full">
        <TabsList className="rounded-none bg-card border border-border p-0 h-auto flex flex-wrap justify-start">
          <TabsTrigger value="staff" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 px-4 py-2" data-testid="tab-staff"><Users className="h-4 w-4" /> Staff Directory</TabsTrigger>
          <TabsTrigger value="financials" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 px-4 py-2" data-testid="tab-financials"><Wallet className="h-4 w-4" /> Financials</TabsTrigger>
          <TabsTrigger value="attendance" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 px-4 py-2" data-testid="tab-attendance"><CalendarCheck className="h-4 w-4" /> Attendance</TabsTrigger>
          <TabsTrigger value="cash" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 px-4 py-2" data-testid="tab-cash"><Coins className="h-4 w-4" /> Cash Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="mt-4"><StaffTab /></TabsContent>
        <TabsContent value="financials" className="mt-4"><FinancialsTab month={month} onSaved={loadOverhead} /></TabsContent>
        <TabsContent value="attendance" className="mt-4"><AttendanceTab onSaved={loadOverhead} /></TabsContent>
        <TabsContent value="cash" className="mt-4"><CashTab month={month} onSaved={loadOverhead} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Staff Directory ---------------- */
function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);
  const [payslip, setPayslip] = useState(null);
  const load = () => api.get("/office/staff").then((r) => setStaff(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const del = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the staff directory?`)) return;
    try { await api.delete(`/office/staff/${s.id}`); toast.success("Staff removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="rounded-none gap-2 font-bold uppercase text-xs" onClick={() => setEditing({})} data-testid="add-staff-btn"><Plus className="h-4 w-4" /> Add Staff</Button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="staff-table">
          <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 text-left">Role</th><th className="px-3 text-left">Contact</th><th className="px-3 text-right">Salary</th><th className="px-3 text-right">Net Payable</th><th className="px-3" /></tr>
          </thead>
          <tbody>
            {staff.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No staff yet. Click <b>Add Staff</b>.</td></tr>}
            {staff.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-muted/50" data-testid={`staff-row-${s.id}`}>
                <td className="px-3 py-2 font-medium">{s.name}</td>
                <td className="px-3 text-muted-foreground">{s.role || "—"}</td>
                <td className="px-3 font-mono-num text-muted-foreground">{s.contact || "—"}</td>
                <td className="px-3 text-right font-mono-num">{money(s.monthly_salary || 0)}{(s.bonus > 0 || s.deduction > 0) && <div className="text-[10px] text-muted-foreground">+{money(s.bonus || 0)} / −{money(s.deduction || 0)}</div>}</td>
                <td className="px-3 text-right font-mono-num font-bold">{money((Number(s.monthly_salary) || 0) + (Number(s.bonus) || 0) - (Number(s.deduction) || 0))}</td>
                <td className="px-3 text-right">
                  <Button size="icon" variant="outline" className="rounded-none h-8 w-8 mr-1" title="Payslip" onClick={() => setPayslip(s)} data-testid={`staff-payslip-${s.id}`}><FileText className="h-4 w-4" /></Button>
                  <Button size="icon" variant="outline" className="rounded-none h-8 w-8 mr-1" onClick={() => setEditing(s)} data-testid={`staff-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" onClick={() => del(s)} data-testid={`staff-del-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <StaffDialog staff={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {payslip && <PayslipDialog staff={payslip} onClose={() => setPayslip(null)} />}
    </div>
  );
}

function PayslipDialog({ staff, onClose }) {
  const [month, setMonth] = useState(monthNow());
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadHist = () => api.get(`/office/payslips?staff_id=${staff.id}`).then((r) => setHist(r.data)).catch(() => {});
  useEffect(() => { loadHist(); /* eslint-disable-next-line */ }, [staff.id]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/office/staff/${staff.id}/payslip?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Payslip-${staff.name}-${month}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Payslip generated & saved to history"); loadHist();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="payslip-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Payslip · {staff.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="space-y-1 flex-1"><Label className="text-xs">Pay Month</Label><Input type="month" className="rounded-none bg-card h-10" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payslip-month" /></div>
            <Button className="rounded-none h-10 gap-2 font-bold uppercase text-xs" onClick={download} disabled={busy} data-testid="payslip-download"><FileText className="h-4 w-4" /> {busy ? "Generating…" : "Generate PDF"}</Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Absence is auto pro-rated on calendar days from the Attendance you marked. Generating a payslip saves that month to the salary history below.</p>
          <div>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-2"><History className="h-4 w-4" /> Salary History</div>
            <div className="border border-border divide-y divide-border max-h-56 overflow-y-auto">
              {hist.length === 0 && <div className="p-3 text-sm text-muted-foreground">No payslips generated yet.</div>}
              {hist.map((h) => (
                <div key={h.month} className="flex items-center gap-3 p-2 px-3 text-sm" data-testid={`payslip-hist-${h.month}`}>
                  <span className="font-mono-num font-bold w-20">{h.month}</span>
                  <span className="text-xs text-muted-foreground flex-1">{h.absent_days}d absent · −{money(h.absence_deduction)}</span>
                  <span className="font-mono-num font-bold text-emerald-500">{money(h.net)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StaffDialog({ staff, onClose, onSaved }) {
  const isNew = !staff.id;
  const [f, setF] = useState({
    name: staff.name || "", role: staff.role || "", contact: staff.contact || "",
    monthly_salary: staff.monthly_salary ?? "", bonus: staff.bonus ?? "", deduction: staff.deduction ?? "",
  });
  const [busy, setBusy] = useState(false);
  const net = (parseFloat(f.monthly_salary) || 0) + (parseFloat(f.bonus) || 0) - (parseFloat(f.deduction) || 0);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    if (f.monthly_salary === "" || isNaN(parseFloat(f.monthly_salary))) return toast.error("Monthly Salary is required");
    setBusy(true);
    try {
      const payload = {
        name: f.name, role: f.role, contact: f.contact,
        monthly_salary: parseFloat(f.monthly_salary) || 0, bonus: parseFloat(f.bonus) || 0, deduction: parseFloat(f.deduction) || 0,
      };
      if (isNew) await api.post("/office/staff", payload); else await api.put(`/office/staff/${staff.id}`, payload);
      toast.success(isNew ? "Staff added" : "Staff updated"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none" data-testid="staff-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{isNew ? "Add Staff" : "Edit Staff"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Name *</Label><Input className="rounded-none bg-card h-10" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name" /></div>
          <div className="space-y-1"><Label className="text-xs">Role</Label><Input className="rounded-none bg-card h-10" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="e.g. Accountant / Driver / Ops" data-testid="staff-role" /></div>
          <div className="space-y-1"><Label className="text-xs">Contact</Label><Input className="rounded-none bg-card h-10 font-mono-num" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} placeholder="Phone / email" data-testid="staff-contact" /></div>
          <div className="space-y-1"><Label className="text-xs">Monthly Salary (₹) *</Label><Input type="number" className="rounded-none bg-card h-10 font-mono-num" value={f.monthly_salary} onChange={(e) => setF({ ...f, monthly_salary: e.target.value })} placeholder="e.g. 25000" data-testid="staff-salary" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Bonus (₹)</Label><Input type="number" className="rounded-none bg-card h-10 font-mono-num" value={f.bonus} onChange={(e) => setF({ ...f, bonus: e.target.value })} placeholder="0" data-testid="staff-bonus" /></div>
            <div className="space-y-1"><Label className="text-xs">Deduction (₹)</Label><Input type="number" className="rounded-none bg-card h-10 font-mono-num" value={f.deduction} onChange={(e) => setF({ ...f, deduction: e.target.value })} placeholder="0" data-testid="staff-deduction" /></div>
          </div>
          <div className="text-sm border-t border-border pt-2">Net Payable: <b className="font-mono-num">{money(net)}</b></div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none font-bold uppercase" onClick={save} disabled={busy} data-testid="staff-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Financials ---------------- */
function FinancialsTab({ month, onSaved }) {
  const [f, setF] = useState({ rent: "", electricity: "", staff_salary: 0, misc: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/office/financials?month=${month}`).then((r) => {
    const d = r.data || {};
    setF({ rent: d.rent || "", electricity: d.electricity || "", staff_salary: d.staff_salary || 0, misc: d.misc || "", note: d.note || "" });
  }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const total = (parseFloat(f.rent) || 0) + (parseFloat(f.electricity) || 0) + (Number(f.staff_salary) || 0) + (parseFloat(f.misc) || 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/office/financials", {
        month, rent: parseFloat(f.rent) || 0, electricity: parseFloat(f.electricity) || 0, misc: parseFloat(f.misc) || 0, note: f.note,
      });
      toast.success(`Fixed costs saved for ${month}`); onSaved?.(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-5 space-y-4 max-w-2xl" data-testid="financials-tab">
      <div className="text-sm text-muted-foreground">Monthly fixed costs for <b className="text-foreground font-mono-num">{month}</b>.</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Office Rent (₹)</Label>
          <Input type="number" className="rounded-none bg-card h-10 font-mono-num" value={f.rent} onChange={(e) => setF((p) => ({ ...p, rent: e.target.value }))} data-testid="fin-rent" /></div>
        <div className="space-y-1"><Label className="text-xs">Electricity Bill (₹)</Label>
          <Input type="number" className="rounded-none bg-card h-10 font-mono-num" value={f.electricity} onChange={(e) => setF((p) => ({ ...p, electricity: e.target.value }))} data-testid="fin-electricity" /></div>
        <div className="space-y-1"><Label className="text-xs">Staff Salary (₹) <span className="text-primary">· auto</span></Label>
          <Input type="number" readOnly className="rounded-none bg-muted h-10 font-mono-num cursor-not-allowed" value={f.staff_salary} data-testid="fin-staff_salary" />
          <p className="text-[10px] text-muted-foreground">Auto-pulled from the Staff Directory (salary + bonus − deduction).</p></div>
        <div className="space-y-1"><Label className="text-xs">Misc. Expenses (₹)</Label>
          <Input type="number" className="rounded-none bg-card h-10 font-mono-num" value={f.misc} onChange={(e) => setF((p) => ({ ...p, misc: e.target.value }))} data-testid="fin-misc" /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Note</Label>
        <Input className="rounded-none bg-card h-10" value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} placeholder="optional" data-testid="fin-note" /></div>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <div className="text-sm">Total Fixed Cost: <b className="font-mono-num text-lg">{money(total)}</b></div>
        <Button className="rounded-none font-bold uppercase" onClick={save} disabled={busy} data-testid="fin-save">{busy ? "Saving…" : "Save Fixed Costs"}</Button>
      </div>
    </div>
  );
}

/* ---------------- Attendance ---------------- */
function AttendanceTab({ onSaved }) {
  const [date, setDate] = useState(dayNow());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/office/attendance?date=${date}`).then((r) => setRows(r.data.staff || [])).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const setStatus = (id, status) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/office/attendance", { date, records: rows.map((r) => ({ staff_id: r.id, status: r.status })) });
      toast.success(`Attendance saved for ${fmtDate(date)}`); onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const present = rows.filter((r) => r.status === "present").length;

  return (
    <div className="space-y-4" data-testid="attendance-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" className="rounded-none bg-card h-10 w-[170px]" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date" /></div>
        <div className="text-sm text-muted-foreground">{present} / {rows.length} present</div>
      </div>
      <div className="border border-border">
        {rows.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No staff to mark. Add staff in the directory first.</div>}
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 p-3 border-b border-border last:border-0" data-testid={`att-row-${r.id}`}>
            <div className="flex-1"><span className="font-medium">{r.name}</span> {r.role && <span className="text-xs text-muted-foreground">· {r.role}</span>}</div>
            <div className="flex gap-1">
              <Button size="sm" variant={r.status === "present" ? "default" : "outline"} className={`rounded-none h-8 gap-1 text-xs ${r.status === "present" ? "bg-emerald-500 hover:bg-emerald-600 text-white" : ""}`} onClick={() => setStatus(r.id, "present")} data-testid={`att-present-${r.id}`}><Check className="h-3.5 w-3.5" /> Present</Button>
              <Button size="sm" variant={r.status === "absent" ? "default" : "outline"} className={`rounded-none h-8 gap-1 text-xs ${r.status === "absent" ? "bg-red-500 hover:bg-red-600 text-white" : ""}`} onClick={() => setStatus(r.id, "absent")} data-testid={`att-absent-${r.id}`}><X className="h-3.5 w-3.5" /> Absent</Button>
            </div>
          </div>
        ))}
      </div>
      {rows.length > 0 && <div className="flex justify-end"><Button className="rounded-none font-bold uppercase" onClick={save} disabled={busy} data-testid="att-save">{busy ? "Saving…" : "Save Attendance"}</Button></div>}
    </div>
  );
}

/* ---------------- Cash Ledger ---------------- */
function CashTab({ month, onSaved }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ date: dayNow(), description: "", amount: "", type: "paid" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/office/cash?month=${month}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const add = async () => {
    const amt = parseFloat(f.amount);
    if (!f.description.trim()) return toast.error("Enter a description");
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/office/cash", { date: f.date, description: f.description.trim(), amount: amt, type: f.type });
      setF({ date: dayNow(), description: "", amount: "", type: "paid" });
      toast.success("Cash entry added"); load(); onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/office/cash/${id}`); load(); onSaved?.(); } catch (e) { toast.error(apiErr(e)); } };

  const paid = rows.filter((r) => r.type !== "received").reduce((a, r) => a + r.amount, 0);
  const received = rows.filter((r) => r.type === "received").reduce((a, r) => a + r.amount, 0);

  return (
    <div className="space-y-4" data-testid="cash-tab">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat label="Cash Paid" value={paid} accent="text-red-500" icon={TrendingDown} />
        <Stat label="Cash Received" value={received} accent="text-emerald-500" icon={TrendingUp} />
        <Stat label="Net Balance" value={received - paid} accent={received - paid >= 0 ? "text-emerald-500" : "text-red-500"} icon={Coins} />
      </div>

      <section className="border border-primary/40 bg-primary/5 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide"><Plus className="h-4 w-4" /> Add Cash Entry</div>
        <div className="grid sm:grid-cols-5 gap-2 items-end">
          <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" className="rounded-none bg-card h-9" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="cash-date" /></div>
          <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Description</Label><Input className="rounded-none bg-card h-9" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="e.g. Tea/snacks, courier, stationery" data-testid="cash-desc" /></div>
          <div className="space-y-1"><Label className="text-xs">Type</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger className="rounded-none h-9 bg-card" data-testid="cash-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="paid">Paid</SelectItem><SelectItem value="received">Received</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Amount (₹)</Label><Input type="number" className="rounded-none bg-card h-9 font-mono-num" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="cash-amount" /></div>
        </div>
        <Button className="rounded-none font-bold uppercase text-xs" onClick={add} disabled={busy} data-testid="cash-add">Add Entry</Button>
      </section>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="cash-table">
          <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 text-left">Description</th><th className="px-3 text-left">Type</th><th className="px-3 text-right">Amount</th><th className="px-3" /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No cash entries for {month}.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/50" data-testid={`cash-row-${r.id}`}>
                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{fmtDate(r.date)}</td>
                <td className="px-3">{r.description}</td>
                <td className="px-3"><span className={`text-[10px] px-2 py-0.5 border uppercase ${r.type === "received" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400"}`}>{r.type}</span></td>
                <td className={`px-3 text-right font-mono-num font-bold ${r.type === "received" ? "text-emerald-500" : ""}`}>{money(r.amount)}</td>
                <td className="px-3 text-right"><Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`cash-del-${r.id}`}><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
